import { useMemo } from 'react';
import {
  mergePlatformChatMessages,
  normalizePlatformMessages,
  type NormalizePlatformMessagesContext,
} from '../../features/chat/chatMessages/platformChatMessageNormalization';
import { createPlatformChatWelcomeMessage } from '../../features/chat/platformChatWelcomeMessage';
import type { ChatMessage } from '../../types/chatMessage';
import { resolveWelcomeFirstName } from './platformChatPageHelpers';

type UsePlatformChatDisplayMessagesOptions = {
  chatId: string | undefined;
  normalizationContext: NormalizePlatformMessagesContext;
  profileFirstName: string | undefined;
  directoryFirstName: string | undefined;
  displayNameFirstName: string | undefined;
  rawMessages: Parameters<typeof normalizePlatformMessages>[0];
  getOptimisticMessages: (sessionId: string) => ChatMessage[];
  hasNextPage: boolean;
  translateWelcome: (firstName: string) => string;
  translateWelcomeNoName: string;
};

export function usePlatformChatDisplayMessages({
  chatId,
  normalizationContext,
  profileFirstName,
  directoryFirstName,
  displayNameFirstName,
  rawMessages,
  getOptimisticMessages,
  hasNextPage,
  translateWelcome,
  translateWelcomeNoName,
}: UsePlatformChatDisplayMessagesOptions): {
  messages: ChatMessage[];
  optimisticMessages: ChatMessage[];
  displayMessages: ChatMessage[];
  messageCount: number;
} {
  const serverMessages = useMemo(
    () => normalizePlatformMessages(rawMessages, normalizationContext),
    [normalizationContext, rawMessages],
  );

  const optimisticMessages = useMemo(
    () => (chatId ? getOptimisticMessages(chatId) : []),
    [chatId, getOptimisticMessages],
  );

  const messages = useMemo(
    () => mergePlatformChatMessages(serverMessages, optimisticMessages),
    [optimisticMessages, serverMessages],
  );

  const welcomeFirstName = resolveWelcomeFirstName({
    profileFirstName,
    directoryFirstName,
    displayNameFirstName,
  });

  const displayMessages = useMemo(() => {
    if (!chatId) {
      return messages;
    }

    // Keep the synthetic greeting only at the true start of the transcript.
    if (hasNextPage) {
      return messages;
    }

    return [
      createPlatformChatWelcomeMessage({
        sessionId: chatId,
        content: welcomeFirstName
          ? translateWelcome(welcomeFirstName)
          : translateWelcomeNoName,
        assistantName: normalizationContext.assistantName,
      }),
      ...messages,
    ];
  }, [
    chatId,
    hasNextPage,
    messages,
    normalizationContext.assistantName,
    translateWelcome,
    translateWelcomeNoName,
    welcomeFirstName,
  ]);

  return {
    messages,
    optimisticMessages,
    displayMessages,
    messageCount: messages.length,
  };
}
